import * as React from "react";
import { observer } from "mobx-react";
import { FlightBarChartProps } from "../../typings";
import { IBarChartOptions, IChartistData } from "chartist";
import { Tooltip, TooltipProps } from "components/shared/Tooltip/Tooltip";
import { FareCalendar } from "typings/microserviceModels/flights-flex-module";
import { UitkSpacing } from "@egds/react-core/spacing";
import { PaginationButton } from "components/flexComponents/Flights/components/FlightBarChart/PaginationButton";
import { TooltipContent } from "components/flexComponents/Flights/components/FlightBarChart/TooltipContent";

export interface FlightBarChartState {
  showTooltip: boolean;
  fareCalendars: FareCalendar[][];
  currentIndex: number;
  tooltipProps?: TooltipProps;
  preButtonDisabled: boolean;
  nextButtonDisabled: boolean;
}

export interface GraphChart {
  container: HTMLDivElement;
  on: (event: string, handler: (data: ChartDrawData) => void) => void;
}

export interface ChartDrawData {
  index: number;
  type: string;
  seriesIndex: number;
  element: ChartNode;
}

export interface ChartNode {
  _node: SVGLineElement;
  addClass: (names: string) => void;
}

@observer
export class FlightBarChat extends React.Component<FlightBarChartProps, FlightBarChartState> {
  private graph: GraphChart;

  constructor(props: FlightBarChartProps) {
    super(props);
    if (this.isFlightBarchartEmpty()) {
      return;
    }
    this.state = {
      showTooltip: false,
      fareCalendars: this.getFareCalendars(),
      currentIndex: 0,
      preButtonDisabled: true,
      nextButtonDisabled: false,
    };
  }

  private isFlightBarchartEmpty() {
    const minimumFlightsToDisplay = 6;
    return !this.props.fareCalendar || this.props.fareCalendar.length < minimumFlightsToDisplay;
  }

  drawBarChart = (index: number): any => {
    import("chartist").then((Chartist: any) => {
      this.graph = this.createGraph(Chartist, index);
      this.graph.on("draw", this.chartDidDraw);
    });
  };

  componentDidMount() {
    if (this.isFlightBarchartEmpty()) {
      return;
    }
    this.drawBarChart(this.state.currentIndex);
  }

  public componentWillUnmount() {
    window.removeEventListener("click", this.showTooltip(0));
    window.removeEventListener("touchmove", this.hideTooltip());
    window.removeEventListener("click", this.hideTooltip());
  }

  getFareCalendars = (): FareCalendar[][] => {
    const fareCalendars: FareCalendar[][] = [];
    const { fareCalendar } = this.props;
    const numOfFlights = fareCalendar.length;
    const numOfFlightsDesktop = 16;
    const numOfFlightsMobile = 6;
    const numOfFlightsPerPage = this.props.isMobile ? numOfFlightsMobile : numOfFlightsDesktop;
    const wrapFlights = (flight: FareCalendar, index: number) => {
      if (index % numOfFlightsPerPage === 0) {
        fareCalendars.push([]);
      }
      fareCalendars[fareCalendars.length - 1].push(flight);
    };

    if (numOfFlights > numOfFlightsPerPage) {
      fareCalendar.map(wrapFlights);
      while (fareCalendars[fareCalendars.length - 1].length < numOfFlightsPerPage) {
        fareCalendars[fareCalendars.length - 1].push(this.generateEmptyFareCalendar());
      }
    } else {
      fareCalendars.push(fareCalendar);
    }

    return fareCalendars;
  };

  generateEmptyFareCalendar = () => {
    return {
      ticketPrice: "",
      formattedTicketPrice: "",
      formattedDepartureDate: "",
      departureDate: "",
      formattedReturnDate: "",
      returnDate: "",
      airlineCode: "",
      airlineName: "",
      originName: "",
      destinationName: "",
      originIataCode: "",
      destinationIataCode: "",
      tripType: "",
      searchUrl: "",
    };
  };

  createGraph = (Chartist: any, index: number) => {
    const selector = `#${this.props.id}`;
    const minSpaceOnAxisY = 25;
    const data: IChartistData = {
      labels: this.chartLabels(index),
      series: this.chartValue(index),
    };

    const options: IBarChartOptions = {
      high: this.maxChartValue(minSpaceOnAxisY),
      low: this.minChartValue(minSpaceOnAxisY),
      axisX: {},
      axisY: {
        offset: this.getOffset(minSpaceOnAxisY),
        labelInterpolationFnc: (value: number) => this.getYAxisValue(value),
        scaleMinSpace: minSpaceOnAxisY,
      },
      distributeSeries: true,
      chartPadding: { right: 0, left: 0 },
    };

    return new Chartist.Bar(selector, data, options);
  };

  public maxChartValue(minSpace: number): number {
    const maxValue = Math.max(...this.props.fareCalendar.map((item) => parseFloat(item.ticketPrice)));

    // Nearest increment above the highest data point
    return Math.round((maxValue + (maxValue * minSpace) / 100) / minSpace) * minSpace;
  }

  public minChartValue(minSpace: number): number {
    const minValue = Math.min(...this.props.fareCalendar.map((item) => parseFloat(item.ticketPrice)));

    // Nearest increment below the lowest data point
    return Math.round((minValue - (minValue * minSpace) / 100) / minSpace) * minSpace;
  }

  public chartValueUnit(): string {
    const valueWithUnit = this.props.fareCalendar[0].formattedTicketPrice;
    if (valueWithUnit.match(/^\d/)) {
      return valueWithUnit.slice(valueWithUnit.search(/\D+$/));
    }
    return valueWithUnit.slice(0, valueWithUnit.search(/\d/));
  }

  public getYAxisValue(value: number): string {
    const unit = this.chartValueUnit();

    return this.priceStartWithDigit() ? value + unit : unit + value;
  }

  private priceStartWithDigit() {
    const samplePrice = this.props.fareCalendar[0].formattedTicketPrice;
    return /^\d/.test(samplePrice);
  }

  public getOffset(minSpace: number): number {
    const yOffsetPerChar = 9;
    const maxLabel = this.chartValueUnit() + this.maxChartValue(minSpace);

    return maxLabel.length * yOffsetPerChar;
  }

  public chartLabels(index: number): string[] {
    return this.state.fareCalendars[index].map((item) => this.convertDate(item.departureDate));
  }

  public convertDate = (date: string): string => {
    const dateObj = new Date(date);

    return !isNaN(dateObj.getMonth()) ? `${dateObj.getMonth() + 1}/${dateObj.getDate()}` : "";
  };

  public chartValue(index: number): number[] {
    return this.state.fareCalendars[index].map((item) => parseFloat(item.ticketPrice));
  }

  chartDidDraw = ({ element, seriesIndex }: ChartDrawData) => {
    if (seriesIndex !== undefined) {
      element._node.addEventListener("click", this.showTooltip(seriesIndex));
      element._node.addEventListener("touchmove", this.hideTooltip());
      document.addEventListener("click", this.hideTooltip());
    }
  };

  showTooltip = (seriesIndex: number) => {
    return (event: MouseEvent & { target: SVGLineElement }) => {
      this.clearSelected();
      const barElement = event.target as SVGLineElement;
      barElement.classList.add("selected");
      const container = this.graph.container;
      const lineBox = barElement.getBBox();
      event.stopPropagation();

      this.setState({
        showTooltip: true,
        tooltipProps: {
          width: container.clientWidth,
          left: lineBox.x,
          top: lineBox.y,
          /* eslint-disable-next-line react/no-access-state-in-setstate */
          content: <TooltipContent {...this.state.fareCalendars[this.state.currentIndex][seriesIndex]} />,
        },
      });
    };
  };

  hideTooltip = () => {
    return (event: MouseEvent & { target: SVGLineElement }) => {
      this.clearSelected();
      this.setState({
        showTooltip: false,
      });
    };
  };

  private clearSelected = () => {
    const container = this.graph.container;
    const bars = Array.from(container.getElementsByClassName("ct-bar"));
    bars.map((bar) => bar.classList.remove("selected"));
  };

  onPrevCallback = () => {
    this.drawBarChart(this.state.currentIndex - 1);
    if (this.state.currentIndex === 1) {
      this.setState({
        preButtonDisabled: true,
      });
    }
    this.setState({
      /* eslint-disable-next-line react/no-access-state-in-setstate */
      currentIndex: this.state.currentIndex - 1,
      nextButtonDisabled: false,
      showTooltip: false,
    });
  };

  onNextCallback = () => {
    this.drawBarChart(this.state.currentIndex + 1);
    if (this.state.currentIndex === this.state.fareCalendars.length - 2) {
      this.setState({
        nextButtonDisabled: true,
      });
    }
    this.setState({
      /* eslint-disable-next-line react/no-access-state-in-setstate */
      currentIndex: this.state.currentIndex + 1,
      preButtonDisabled: false,
      showTooltip: false,
    });
  };

  public render() {
    if (this.isFlightBarchartEmpty()) {
      return null;
    }
    const { id } = this.props;
    const { showTooltip, tooltipProps } = this.state;

    return (
      <UitkSpacing padding={{ block: "four", inline: "four" }}>
        <div className="FlightsBarChart uitk-card" data-testid="flights-bar-chart">
          <div className="chartContainer">
            {showTooltip && tooltipProps && <Tooltip {...tooltipProps} />}
            <div id={id} className="chart" />
            <PaginationButton
              numOfPages={this.state.fareCalendars.length}
              preButtonDisabled={this.state.preButtonDisabled}
              nextButtonDisabled={this.state.nextButtonDisabled}
              onPrevCallback={this.onPrevCallback}
              onNextCallback={this.onNextCallback}
            />
          </div>
        </div>
      </UitkSpacing>
    );
  }
}

export default FlightBarChat;
